import React, { useState, useEffect } from 'react';
import { isMobile } from '@buzzfeed/bf-utils/lib/device';
import { triggerEnhancedWebviewFeature } from '@buzzfeed/bf-utils/lib/webview';
import { addParams } from '@buzzfeed/bf-utils/lib/query-string';
import { useTrackingContext, useViewImpression } from '../../../hooks';
import styles from './index.module.scss';
import { CrownIcon, CreateIcon, StopwatchIcon } from '../../../icons/Icons';
import IslandButton from '../../IslandButton';

const ChallengeCard = ({
  item,
  accentColor = 'yellow',
  packageType = 'challenge',
  isWeb,
  isStandaloneCard = false,
  onWebNavigate,
  sharedTrackingInfo,
}) => {
  const { trackInternalLink } = useTrackingContext();
  const isChallenge = packageType === 'challenge';

  const [isMobileDevice, setIsMobileDevice] = useState(
    typeof window !== 'undefined' && isMobile()
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data extraction
  const title = item?.content?.bfp?.data?.intro?.card?.title || '';
  const imageUrl =
    item?.content?.bfp?.data?.intro?.card?.backgroundImage?.url || '';
  const creationCount =
    item?.content?.comments?.by_type?.ai_generation?.count || 0;
  const timestamp = item?.created_at; // Unix timestamp in seconds
  const ctaText = item?.content?.bfp?.data?.intro?.card?.buttonCTA || 'View';
  const itemId = item?.id;
  const itemType = item?.object_type || 'bfp_content';

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Complete tracking data (shared info + item-specific fields)
  const trackingData = {
    ...sharedTrackingInfo,
    item_type: 'card',
    item_name: itemId,
    target_content_type: 'widget',
    target_content_id: itemId,
  };

  // View impression tracking
  const { setObservable } = useViewImpression({
    trackingData,
  });

  const onNavigate = () => {
    // Simple tracking using parent's shared info + item-specific fields
    trackInternalLink(trackingData);

    if (isWeb) {
      onWebNavigate(itemType, itemId);
      return;
    }

    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/webview/feed/content-objects/${itemType}/${itemId}`,
        queryParams: {
          source: 'page',
          type: 'creation',
        },
      },
      version: 1,
    });
  };

  const hasBottomFullWidthBtn = isWeb && isStandaloneCard && !isMobileDevice;

  return (
    <div
      className={`${styles.challengeCard} ${isWeb ? styles.web : ''} ${isStandaloneCard ? styles.standalone : ''}`}
      style={{
        '--accent-color': `var(--bfi-${accentColor})`,
        '--accent-text-color': `var(--bfi-${accentColor}-text)`,
      }}
      ref={(el) => setObservable(el)}
    >
      <div className={styles.cardContent}>
        {imageUrl && (
          <img
            src={addParams(imageUrl, { resize: '650:*' })}
            alt={title}
            className={styles.image}
            loading="lazy"
          />
        )}
        <div className={styles.container}>
          <div className={styles.content}>
            <header
              className={`${styles.header} ${styles[`header--${packageType}`] || ''}`}
            >
              {isChallenge && (
                <div className={styles.challengeLabel}>
                  <div className={styles.challengeIconContainer}>
                    <CrownIcon />
                    <span>Challenge</span>
                  </div>
                </div>
              )}
              <div className={styles.likes}>
                <CreateIcon />
                <span>{creationCount}</span>
              </div>
            </header>

            <h3 className={styles.challengeTitle}>{title}</h3>

            <div className={styles.meta}>
              <span className={styles.timestamp}>
                <StopwatchIcon />
                {formatTimestamp(timestamp)}
              </span>
            </div>
          </div>

          {!hasBottomFullWidthBtn && (
            <IslandButton
              text={ctaText}
              onClick={onNavigate}
              variant="light"
              bgColor="var(--accent-color)"
              color="var(--accent-text-color)"
              size={
                isWeb && !isStandaloneCard
                  ? 'small'
                  : isWeb && !isMobileDevice
                    ? 'large'
                    : 'small'
              }
              fullWidth
            />
          )}
        </div>
      </div>

      {hasBottomFullWidthBtn && (
        <IslandButton
          text={ctaText}
          onClick={onNavigate}
          variant="light"
          bgColor="var(--accent-color)"
          color="var(--accent-text-color)"
          size="xl"
          fullWidth
        />
      )}
    </div>
  );
};

export default ChallengeCard;
